'use client';

import React, { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import type { CartItemGroupProps, BaseCartItemInterface } from './cart-item-group.types';
import { cn } from '../../utils/cn';
import { Checkbox } from '../../atoms/checkbox';
import { SwipeableCard } from '../../molecules/swipeable-card';
import { Button } from '../../atoms/button';
import { Text } from '../../atoms/text';
import { CartItemV2 } from '../cart-item-v2';
import styles from './cart-item-group.module.css';
import { FlatList } from '../../molecules/flat-list';
import { Trash2 } from 'lucide-react';
import { CartItemGroupVariantEnum } from './cart-item-group.enum';
import type { SizeOptions } from '../../../theme';
import { type ButtonComponentProps } from '../../atoms/button/button';

/**
 * CartItemGroup - Base UI component for cart item grouping
 *
 * This is the UI Core base component matching mobile ui-react-native-core CartItemGroup.
 * It handles:
 * - Checkbox for selecting cart item group
 * - Swipeable card for delete action
 * - List of cart items rendering
 * - Out of stock button display
 * - After cart item group component slot
 *
 * Business logic (stock availability, auto-unselect) should be handled by wrapper components.
 *
 * @see packages/ui-react-native-core/src/components/organisms/cart-v2/cart-item-group/cart-item-group.tsx
 */
export const CartItemGroup: FC<CartItemGroupProps> = ({
    cartId,
    cartCounterButtonProps,
    showOnlySelected = false,
    disabled = false,
    containerClassName,
    swipableClassName,
    onSelected,
    onUnSelected,
    onItemPressed,
    onRightActionPressed,
    onOutOfStockPress,
    outOfStockButtonText,
    AfterCartItemGroupComponent,
    cartItemReference,
    CartItemComponent,
    isSwipeable = false,
    variantCounterButton,
    variantCartItemV2,
    variantSizePriceBox,
    variant = CartItemGroupVariantEnum.COMPACT,
}) => {
    const [checked, setChecked] = useState(false);
    const cardMode = useMemo(() => (showOnlySelected ? 'contained' : 'elevated'), [showOnlySelected]);
    const isFull = useMemo(() => variant === CartItemGroupVariantEnum.FULL, [variant]);
    const shouldShowCheckbox = useMemo(() => !showOnlySelected, [showOnlySelected]);

    // Filter out null or undefined items
    const filteredItems: BaseCartItemInterface[] = useMemo(() => {
        if (!cartItemReference?.cartItems) {
            return [];
        }
        return cartItemReference?.cartItems?.filter((item): item is BaseCartItemInterface => item != null);
    }, [cartItemReference?.cartItems]);

    const CartItemToRender = useMemo(() => CartItemComponent || CartItemV2, [CartItemComponent]);
    const isMini = useMemo(() => variant === CartItemGroupVariantEnum.MINI, [variant]);

    const deleteButtonProps = useMemo<ButtonComponentProps>(
        () => ({
            children: isMini ? 'Xóa' : <Trash2 size={16} />,
            className: isMini ? styles.miniDelete : styles.iconDelete,
        }),
        [isMini]
    );

    const handleRightActionPressed = useCallback(() => {
        onRightActionPressed?.(cartItemReference);
    }, [onRightActionPressed, cartItemReference]);

    // Determine if the error should be shown at the product (CartItemV2) level.
    // Gift-related errors (GIFT_*) are displayed at the action/gift level by
    // AfterCartItemGroupComponent → CartGiftList, so suppress them here to avoid duplication.
    const productLevelErrorMessage = useMemo(() => {
        const error = cartItemReference?.error;
        if (!error?.message) return undefined;
        if (AfterCartItemGroupComponent && error.errorCode?.startsWith('GIFT_')) {
            return undefined;
        }
        return error.message;
    }, [cartItemReference?.error, AfterCartItemGroupComponent]);

    const renderItem = useCallback(
        (data: { item: BaseCartItemInterface }) => {
            const { item } = data;
            return (
                <CartItemToRender
                    key={item?.id}
                    testId="cart-item"
                    count={cartItemReference?.qty ?? 0}
                    item={item}
                    disabled={disabled}
                    cartCounterButtonProps={cartCounterButtonProps}
                    onItemPressed={onItemPressed}
                    cartItemReference={cartItemReference}
                    errorMessage={productLevelErrorMessage}
                    variantCounterButton={variantCounterButton}
                    variantSizePriceBox={variantSizePriceBox}
                    variant={variantCartItemV2}
                    onDelete={handleRightActionPressed}
                    buttonProps={isFull || isMini ? deleteButtonProps : undefined}
                    disabledButtonDelete={showOnlySelected}
                    variantCartItemGroup={variant}
                    showOnlySelected={showOnlySelected}
                />
            );
        },
        [
            CartItemToRender,
            cartItemReference,
            disabled,
            cartCounterButtonProps,
            onItemPressed,
            productLevelErrorMessage,
            variantCounterButton,
            variantCartItemV2,
            variantSizePriceBox,
            handleRightActionPressed,
            isFull,
            deleteButtonProps,
            showOnlySelected,
            variant,
            isMini,
        ]
    );

    useEffect(() => {
        // Reset checked state when cartItemReference changes
        setChecked(cartItemReference?.isSelected ?? false);
    }, [cartItemReference?.cartItemId, cartItemReference?.isSelected]);

    const onCheckboxPressed = useCallback(
        (newStatus: boolean) => {
            if (newStatus) {
                onSelected?.({ cartItemReference: cartItemReference });
            } else {
                onUnSelected?.({ cartItemReference: cartItemReference });
            }
        },
        [onSelected, onUnSelected, cartItemReference]
    );

    const handleOutOfStockActionPressed = useCallback(() => {
        if (filteredItems?.length) {
            // Sản phẩm luôn là đầu danh sách
            onOutOfStockPress?.(filteredItems[0]);
        }
    }, [onOutOfStockPress, filteredItems]);

    // Right action button props for swipeable - matching mobile SwipeableRightAction behavior
    const rightButtonProps = useMemo(
        () => ({
            onClick: handleRightActionPressed,
            children: 'Xoá',
            className: styles.deleteButton,
        }),
        [handleRightActionPressed]
    );

    const variantClass = useMemo(
        () => (variant === CartItemGroupVariantEnum.COMPACT ? styles.compact : styles.full),
        [variant]
    );

    const swipeableContainerClass = useMemo(
        () =>
            cn(
                styles.container,
                variantClass,
                disabled && styles.containerDisabled,
                !isSwipeable && styles.isSwipeable,
                swipableClassName
            ),
        [disabled, swipableClassName, variantClass, isSwipeable]
    );

    const cartContainerClass = useMemo(
        () => cn(styles.cart, containerClassName, cardMode === 'elevated' ? styles.cardElevated : styles.cardContained),
        [containerClassName, cardMode]
    );

    const afterCartItemGroupSize: SizeOptions = useMemo(() => {
        return isFull ? 'large' : 'small';
    }, [isFull]);

    const afterCartItemGroupContainer = useMemo(() => {
        return cn(shouldShowCheckbox && styles.afterCardItemGroupContainer);
    }, [shouldShowCheckbox]);

    const afterCartItemGroup = useMemo(() => {
        if (!AfterCartItemGroupComponent) return null;

        return (
            <div className={afterCartItemGroupContainer}>
                <AfterCartItemGroupComponent
                    className={styles.afterCardItemGroup}
                    cartId={cartId}
                    disabled={disabled}
                    variant={variant}
                    cartItemReference={cartItemReference}
                    size={afterCartItemGroupSize}
                    showActionButton={showOnlySelected}
                />
            </div>
        );
    }, [
        AfterCartItemGroupComponent,
        afterCartItemGroupContainer,
        cartId,
        disabled,
        variant,
        cartItemReference,
        afterCartItemGroupSize,
        showOnlySelected,
    ]);

    const outOfStockAction = useMemo(() => {
        if (!disabled || !outOfStockButtonText) return null;
        return (
            <div className={styles.containerOutOfStock}>
                <Button
                    data-testid="out-of-stock-action"
                    className={styles.btnOutOfStock}
                    variant="outlined"
                    onClick={handleOutOfStockActionPressed}
                >
                    <Text size="medium" align="right" className={styles.textButton}>
                        {outOfStockButtonText}
                    </Text>
                </Button>
            </div>
        );
    }, [disabled, outOfStockButtonText, handleOutOfStockActionPressed]);

    const cartContent = useMemo(() => {
        return (
            <div className={styles.cartContent}>
                {shouldShowCheckbox && (
                    <div className={styles.checkBox}>
                        <Checkbox
                            data-testid="cart-item-group-checkbox"
                            checked={checked}
                            disabled={disabled}
                            onCheckedChange={onCheckboxPressed}
                            size="medium"
                        />
                    </div>
                )}

                <FlatList data={filteredItems} renderItem={renderItem} className={styles.containerFlatList} />
            </div>
        );
    }, [shouldShowCheckbox, checked, disabled, onCheckboxPressed, filteredItems, renderItem]);

    return (
        <SwipeableCard
            isSwipeable={isSwipeable}
            containerClassName={swipeableContainerClass}
            buttonProps={rightButtonProps}
        >
            <div className={cartContainerClass}>
                <div className={styles.list}>
                    {cartContent}
                    {afterCartItemGroup}
                    {outOfStockAction}
                </div>
            </div>
        </SwipeableCard>
    );
};
